import React from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import { MapsHomeWork, Launch } from '@mui/icons-material';
import { canAccessExplorers } from '../../../utils/explorerAccess';
import { formatSchoolName } from '../../../utils/schoolUtils';
import { detectSchoolDataType } from '../../../utils/schoolDataDetection';
import { calculateAverageEnrollmentChange, filterSchoolsWithEnrollmentData } from '../../../utils/areaCalculations';

const ComparisonAnalysis = ({ 
  selectedSchool,
  selectedSchoolTrends,
  schoolData,
  nearbySchools,
  schoolsEnrollmentData,
  selectedGrades,
  selectedDriveTime,
  user,
  isPublic = false
}) => {
  if (!selectedSchool) {
    return null;
  }

  // Detect what type of data is available for this school/location
  const { hasEnrollmentData, isDropAPinLocation } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);
  
  // For standard schools, we need the trends data and school data
  if (hasEnrollmentData && (!selectedSchoolTrends || !schoolData)) {
    return null;
  }

  // Calculate rank based on enrollment changes
  const getRank = () => {
    const sortedSchools = [...schoolData].sort((a, b) => {
      const changeA = a.currentEnrollment && a.previousEnrollment ? 
        ((a.currentEnrollment - a.previousEnrollment) / a.previousEnrollment) * 100 : -Infinity;
      const changeB = b.currentEnrollment && b.previousEnrollment ? 
        ((b.currentEnrollment - b.previousEnrollment) / b.previousEnrollment) * 100 : -Infinity;
      return changeB - changeA;
    });
    return sortedSchools.findIndex(s => s.uuid === selectedSchool.uuid) + 1;
  };

  // Get ordinal suffix
  const getOrdinalSuffix = (number) => {
    const j = number % 10;
    const k = number % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  // Create URL with school parameter
  const explorerUrl = `/geographic-explorer?school=${selectedSchool.uuid}`;

  // Generate appropriate message based on data availability
  const getMessage = () => {
    if (isDropAPinLocation) {
      // Check if nearby schools data is available at all
      if (!nearbySchools || Object.keys(nearbySchools.current || {}).length === 0) {
        return (
          <>
            <strong>{formatSchoolName(selectedSchool.name)}</strong> is in a location where nearby schools data is not available for comparison analysis.
          </>
        );
      }
      
      // Show nearby school performance without ranking the selected location
      const driveTime = selectedDriveTime || 10;
      const grades = selectedGrades || ['Kindergarten', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
      
      const nearbySchoolsForDriveTime = nearbySchools?.current?.[driveTime] || [];
      const nearbySchoolsWithData = filterSchoolsWithEnrollmentData(nearbySchoolsForDriveTime, schoolsEnrollmentData);
      
      if (nearbySchoolsWithData.length === 0) {
        return (
          <>
            <strong>{formatSchoolName(selectedSchool.name)}</strong> is located in an area with limited nearby school enrollment data for comparison analysis.
          </>
        );
      }

      const enrollmentStats = calculateAverageEnrollmentChange(nearbySchoolsWithData, schoolsEnrollmentData, grades);
      const averageChange = Math.round(enrollmentStats.averageChange);
      const trend = averageChange > 0 ? 'growth' : 'decline';
      
      return (
        <>
          <strong>{formatSchoolName(selectedSchool.name)}</strong> is surrounded by <strong>{nearbySchoolsWithData.length}</strong> school{nearbySchoolsWithData.length !== 1 ? 's' : ''} within a {driveTime}-minute drive. 
          These nearby schools have seen an average enrollment change of <strong>{averageChange > 0 ? '+' : ''}{averageChange}%</strong> between 2019-20 and 2024-25, 
          indicating {trend} in the local education market.
        </>
      );
    }

    // Existing school ranking logic for schools with enrollment data
    const percentChange = selectedSchoolTrends?.selectedSchool?.percentChange;
    const rank = getRank();

    // A null change means no baseline exists, so there is nothing to rank. Rounding it to
    // zero would place the school mid-table as though it had held steady.
    if (percentChange === null || percentChange === undefined) {
      return (
        <>
          <strong>{formatSchoolName(selectedSchool.name)}</strong> has no earlier enrollment on record, so its change cannot be ranked against the {schoolData.length} nearby schools.
        </>
      );
    }

    return (
      <>
        <strong>{formatSchoolName(selectedSchool.name)}</strong>'s enrollment change of <strong>{Math.round(percentChange)}%</strong> ranks <strong>{rank}{getOrdinalSuffix(rank)}</strong> out of {schoolData.length} nearby schools between 2019-20 and 2024-25.
      </>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper 
        elevation={0}
        sx={{
          mb: 2,
          py: 2,
          px: 3,
          backgroundColor: 'grey.50',
          color: 'text.primary',
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 6,
            backgroundColor: 'primary.main',
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8
          }
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h8" sx={{ fontWeight: 200 }}>
            {getMessage()}
          </Typography>
        </Box>
        {!isPublic && canAccessExplorers(user?.role) && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<MapsHomeWork />}
            endIcon={<Launch />}
            href={explorerUrl}
            sx={{ 
              backgroundColor: 'primary.dark',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.main'
              }
            }}
          >
            OPEN IN GEOGRAPHIC EXPLORER
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default ComparisonAnalysis;